import React, { ForwardedRef, forwardRef, ReactNode } from 'react';
import { Slide, styled, Theme } from '@mui/material';
import {
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

import Typography from '../Typography/index.js';
import XIcon from '../Icon/X.js';

import { HStack as ModalHeader, VStack } from '../Layout/index.js';
import { MIN_WIDTH, useMedia } from '../../hooks/index.js';

type ModalSize = 'small' | 'medium' | 'large';

export interface ModalProps extends Pick<MuiDialogProps, 'open' | 'onClose'> {
  /**
   * The content of the body part of the modal.
   */
  children: ReactNode;

  /**
   * @ignore
   * Classes applied to the MuiDialog component root.
   */
  className?: string;

  /**
   * If true, the modal will be full screen on modal. Otherwise, it will take up the height of the content.
   * @default true
   */
  isFullScreenOnMobile?: boolean;

  /**
   * The size of the modal in width.
   * Has no effect on the modal on mobile.
   *
   * @default "medium"
   */
  size?: ModalSize;

  /**
   * The title of the Modal.
   */
  title: ReactNode;
}

const Transition = forwardRef(
  (
    props: TransitionProps & { children: React.ReactElement<any, any> },
    ref: React.Ref<unknown>,
  ) => {
    return <Slide direction="up" ref={ref} {...props} />;
  },
);

const sizeToMaxWidth: Record<ModalSize, number> = {
  small: 504,
  medium: 680,
  large: 1032,
};

const StyledMuiDialog = styled(MuiDialog, {
  shouldForwardProp: (prop: string) =>
    !['isFullScreenOnMobile', 'size'].includes(prop),
})<Omit<ModalProps, 'title'>>(
  ({
    isFullScreenOnMobile,
    size,
    theme,
  }: Pick<ModalProps, 'isFullScreenOnMobile' | 'size'> & { theme: Theme }) => ({
    '& .MuiPaper-root': {
      boxSizing: 'border-box',
      margin: 0,
      border: '1px solid #d9d9d9',
      borderRadius: '4px',
      boxShadow: '0 0 16px rgba(37, 39, 82, 0.1)',
      width: 'calc(100% - 96px)',
      maxWidth: sizeToMaxWidth[size!],
      overflow: 'initial',

      [theme.breakpoints.down('sm')]: {
        position: 'absolute',
        ...(isFullScreenOnMobile ? { top: '64px' } : {}),
        bottom: 0,
        width: '100%',
        maxWidth: 'unset',
        maxHeight: 'calc(100% - 64px)',
        borderRadius: 'unset',
      },
    },
  }),
);

const ModalBody = styled(VStack)`
  scrollbar-gutter: stable;
`;

/**
 * Modals are used within our apps to either give the user a lot of extra detail
 * (i.e. a contextual helper) or to allow the user to do something functional
 * in the product, while allowing them to remain on the page.
 *
 * @see https://mui.com/material-ui/react-modal/
 */
const Modal = forwardRef(
  (
    {
      children,
      className,
      isFullScreenOnMobile = true,
      onClose,
      open,
      size = 'medium',
      title,
    }: ModalProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const isTabletOrLarger = useMedia(MIN_WIDTH.MD);

    return (
      <StyledMuiDialog
        className={className}
        open={open}
        TransitionComponent={isTabletOrLarger ? undefined : Transition}
        onClose={onClose}
        ref={ref}
        fullWidth
        scroll="paper"
        isFullScreenOnMobile={isFullScreenOnMobile!}
        size={size!}
      >
        <ModalHeader
          borderBottom={1}
          borderColor="#d9d9d9"
          justifyContent="space-between"
          alignItems="center"
          paddingX={24}
          paddingY={12}
        >
          <Typography bold variant="title-large">
            {title}
          </Typography>
          {onClose && (
            <XIcon
              size="large"
              color="#595959"
              cursor="pointer"
              onClick={onClose}
            />
          )}
        </ModalHeader>
        <ModalBody
          height="100%"
          justifyContent="space-between"
          gap={12}
          padding={24}
          overflow="auto"
        >
          {children}
        </ModalBody>
      </StyledMuiDialog>
    );
  },
);

Modal.displayName = 'Modal';

export default Modal;
